import { z } from "zod";
import { IdentityEmbedSchema } from "./Identity.js";

/**
 * List of valid file types for document upload, based on what our pipeline ingestion
 * can handle.
 */
export const supportedDocumentMimeTypes = [
  "text/plain", // .txt (plain ASCII text)
  "text/markdown", // .md (Markdown)
  "text/html", // .html (HTML)
  "application/msword", // .doc (Microsoft Word older binary)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx (Word OpenXML)
  "text/csv", // .csv (Comma-separated values)
  "application/vnd.ms-excel", // .xls (Excel older binary)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx (Excel OpenXML)
  "application/pdf", // .pdf (Portable Document Format)
] as const;

/**
 * List of valid file types for google drive document upload, based on what type
 * we are able to process (e.g. from Nango <> Google Drive integration).
 */
export const supportedGDriveDocumentMimeTypes = [
  // Google-native formats
  // "application/vnd.google-apps.document", // Google Docs
  "application/vnd.google-apps.spreadsheet", // Google Sheets
] as const;

export enum DocumentStorageProvider {
  S3 = "S3",
  GoogleDrive = "Google Drive",
}

export const DocumentSchema = z.object({
  id: z.uuid(),
  createdAt: z.date(),
  uploader: IdentityEmbedSchema,

  /**
   * Where the file is stored (e.g S3)
   */
  storageProvider: z.enum(DocumentStorageProvider),

  /**
   * Fully-qualified identifier to access this file in the given provider, e.g an S3 ARN
   */
  storagePath: z.string(),
  originalFileName: z.string(),
  mimeType: z
    .string()
    .refine(
      (v) =>
        (
          [
            ...supportedDocumentMimeTypes,
            ...supportedGDriveDocumentMimeTypes,
          ] as unknown as string[]
        ).includes(v),
      {
        error: "Not a valid input mime type",
      },
    ),

  /**
   * If available, a full text representation of the document
   */
  textContent: z.string().nullable(),

  /**
   * If available, a short generated summary of the document
   */
  contentSummary: z.string().nullable(),

  /**
   * If available, a user-provided description of the document
   */
  description: z.string().nullable(),
});

/**
 * Metadata required to actually start the upload procedure
 */
export const DocumentUploadMetadataSchema = z.object({
  mimeType: DocumentSchema.shape.mimeType,
  fileName: z.string().max(1024),
});

/**
 * After uploading, the client notifies us with the granted URL, which we validate
 * and use to create a Document record:
 */
export const CommitDocumentSchema = z.object({
  ...DocumentUploadMetadataSchema.shape,
  storagePath: z.string(),
});

export const CreateDocumentSchema = z.object({
  fileName: DocumentSchema.shape.originalFileName,
  mimeType: DocumentSchema.shape.mimeType,
  storageProvider: DocumentSchema.shape.storageProvider,
  storagePath: DocumentSchema.shape.storagePath,
});

export type DocumentUploadMetadata = z.infer<
  typeof DocumentUploadMetadataSchema
>;

export type CommitDocument = z.infer<typeof CommitDocumentSchema>;
export type CreateDocument = z.infer<typeof CreateDocumentSchema>;
export type Document = z.infer<typeof DocumentSchema>;
